/**
 * PRB-14 — wafer probe and sort.
 *
 * The tool where the wafer stops being a process and becomes a number. Every
 * decision taken upstream arrives here at once, converted into a single figure
 * that the whole building is judged by, and the technician's job is to read it
 * as a picture rather than as a score.
 *
 * YIELD IS A STATISTICAL MODEL, AND WHICH ONE MATTERS
 *
 * Scatter D killer defects per square centimetre at random over a die of area
 * A, and the chance a die catches none of them is Poisson:
 *
 *     Y = exp(-D*A)
 *
 * That is the model everyone learns and it is systematically pessimistic,
 * because defects are not random - they cluster. A scratch, a handling mark, an
 * edge ring, a chamber that spat particles for one hour: all of them put many
 * defects on a few die rather than a few on many. Clustered defects waste
 * themselves on die that were already dead, so the same total defect count
 * yields BETTER. The negative binomial captures it with one clustering
 * parameter:
 *
 *     Y = (1 + D*A/alpha)^(-alpha)
 *
 * and as alpha goes to infinity it becomes Poisson again. A fab that predicts
 * with Poisson will under-forecast its own yield, and by more the worse its
 * clustering is - which is a strange and useful thing to know.
 *
 * WHY THE MAP MATTERS MORE THAN THE NUMBER
 *
 * A mean yield of 78% can be a uniform 78% everywhere, or 95% in the middle and
 * dead at the edge, or a perfect wafer with a scratch across it. Those are
 * three different problems with three different owners, and the number cannot
 * tell them apart. The map can.
 */

import { clamp, normalCdf } from '../../core/math'

/** Wafer radius [mm]. */
const WAFER_RADIUS_MM = 150

export type YieldModel = 'poisson' | 'negative-binomial'

export interface ProbeRecipe {
  /** Die area [cm^2]. The single biggest lever on yield, and design owns it. */
  dieAreaCm2: number
  /** Die inside this distance of the wafer edge are not probed [mm]. */
  edgeExclusionMm: number
  /** Which yield model the fab predicts with. */
  model: YieldModel
  /** Clustering parameter for the negative binomial. Low means heavy clustering. */
  clusterAlpha: number
  /** Probe card touchdowns before the tips need cleaning. */
  touchdownsPerClean: number
  /** Parametric test limit as a fraction of the spec width. */
  parametricGuardBand: number
  /** Test time per touchdown [s]. */
  testSeconds: number
}

export const DEFAULT_PROBE_RECIPE: ProbeRecipe = {
  dieAreaCm2: 0.59,
  edgeExclusionMm: 3,
  model: 'negative-binomial',
  clusterAlpha: 3,
  touchdownsPerClean: 2000,
  parametricGuardBand: 0.08,
  testSeconds: 1.8,
}

export const PROBE_LIMITS: Record<
  | 'dieAreaCm2'
  | 'edgeExclusionMm'
  | 'clusterAlpha'
  | 'touchdownsPerClean'
  | 'parametricGuardBand'
  | 'testSeconds',
  { min: number; max: number; step: number; unit: string; label: string }
> = {
  dieAreaCm2: { min: 0.02, max: 8, step: 0.01, unit: 'cm²', label: 'Die area' },
  edgeExclusionMm: { min: 0, max: 15, step: 0.5, unit: 'mm', label: 'Edge exclusion' },
  clusterAlpha: { min: 0.4, max: 30, step: 0.2, unit: 'α', label: 'Clustering' },
  touchdownsPerClean: { min: 200, max: 20000, step: 100, unit: '', label: 'Touchdowns per clean' },
  parametricGuardBand: { min: 0, max: 0.3, step: 0.01, unit: '', label: 'Parametric guard band' },
  testSeconds: { min: 0.2, max: 20, step: 0.1, unit: 's', label: 'Test time per device' },
}

export interface ProbeCondition {
  /** Probe tip wear, 0 new to 1 worn. Worn tips add contact resistance. */
  tipWear: number
  /** Planarity error across the card [um]. Some tips land light, some heavy. */
  cardPlanarityUm: number
  /** Chuck temperature offset [C]. Parametrics are temperature-sensitive. */
  chuckOffsetC: number
  /** Measurement repeatability, as a fraction of the parametric spec width. */
  gaugeSigma: number
  /**
   * Vacuum holding the wafer to the chuck [kPa below atmosphere]. Written by
   * the house model: the pump that makes it stands in the sub-fab, and no
   * repair on this tool restores it. A wafer held weakly lifts at its edge,
   * and a lifted edge lands light under the card exactly as a card out of
   * plane does.
   */
  chuckVacuumKpa: number
}

export const NOMINAL_PROBE_CONDITION: ProbeCondition = {
  tipWear: 0,
  cardPlanarityUm: 3,
  chuckOffsetC: 0,
  gaugeSigma: 0.03,
  chuckVacuumKpa: 80,
}

/**
 * How far the wafer's edge lifts off a chuck that is not holding it [µm]:
 * nothing above 55 kPa, then about 0.6 µm per kPa of vacuum lost. Adds to
 * the card's own planarity error, because the card cannot tell a lifted
 * wafer from a tilted card.
 */
export function waferLiftUm(chuckVacuumKpa: number): number {
  return Math.max(0, 55 - chuckVacuumKpa) * 0.6
}

export type MapSignature = 'uniform' | 'edge' | 'centre' | 'radial' | 'clustered'

export interface ProbeResult {
  /** Die on the wafer before edge exclusion. */
  grossDie: number
  /** Die actually probed. */
  probedDie: number
  /** Die lost to edge exclusion. */
  edgeLoss: number
  /** Yield against random defects alone. */
  defectYield: number
  /** What Poisson would have predicted, for comparison. */
  poissonYield: number
  /** Yield lost to parametric structures being out of limit. */
  parametricYield: number
  /**
   * Measurement uncertainty the tool is actually running at, as a fraction
   * of the parametric spec width.
   *
   * Reported because it is what a noisy tester DOES. With limits set 92% of
   * the way out, gauge noise never reaches them - so a noisy parametric
   * chain costs almost no average yield and destroys repeatability instead.
   * A tool that bins the same wafer differently on two touchdowns is broken,
   * and the yield number is the last place it will show.
   */
  effectiveGaugeSigma: number
  /** Yield lost to the probe card rather than to the wafer. */
  contactYield: number
  /** Overall sort yield. */
  sortYield: number
  /** Good die shipped to assembly. */
  goodDie: number
  /** What the map looks like, which is the actual diagnostic. */
  signature: MapSignature
  /** Good die at the centre and at the edge, for the map. */
  centreYield: number
  edgeYield: number
  /** Wafer test time [h]. */
  waferHours: number
}

/** Poisson yield: defects scattered completely at random. */
export const poissonYield = (densityCm2: number, dieAreaCm2: number): number =>
  Math.exp(-Math.max(densityCm2, 0) * dieAreaCm2)

/**
 * Negative binomial yield: defects that cluster.
 *
 * Alpha is how tightly. As alpha grows the clusters dissolve and this becomes
 * Poisson exactly, which is the right way to think about it - Poisson is the
 * special case where nothing clusters, and nothing in a fab does.
 */
export const negativeBinomialYield = (
  densityCm2: number,
  dieAreaCm2: number,
  alpha: number,
): number => Math.pow(1 + (Math.max(densityCm2, 0) * dieAreaCm2) / Math.max(alpha, 0.05), -alpha)

/** How many die of a given area fit on the wafer, less the edge exclusion. */
export function dieCount(dieAreaCm2: number, edgeExclusionMm: number) {
  const usableRadiusCm = (WAFER_RADIUS_MM - edgeExclusionMm) / 10
  const fullRadiusCm = WAFER_RADIUS_MM / 10
  // Packing efficiency: a square die on a round wafer wastes the rim.
  const packing = 0.92
  const gross = Math.floor((Math.PI * fullRadiusCm * fullRadiusCm * packing) / dieAreaCm2)
  const probed = Math.floor((Math.PI * usableRadiusCm * usableRadiusCm * packing) / dieAreaCm2)
  return { gross, probed }
}

export function probe(
  recipe: ProbeRecipe,
  condition: ProbeCondition = NOMINAL_PROBE_CONDITION,
  killerDensityCm2 = 0.03,
): ProbeResult {
  const { gross, probed } = dieCount(recipe.dieAreaCm2, recipe.edgeExclusionMm)

  const poisson = poissonYield(killerDensityCm2, recipe.dieAreaCm2)
  const clustered = negativeBinomialYield(
    killerDensityCm2,
    recipe.dieAreaCm2,
    recipe.clusterAlpha,
  )
  const defectYield = recipe.model === 'poisson' ? poisson : clustered

  // Parametric loss: structures measured against a limit, with the tester's own
  // error widening the tail. The same Gaussian trade FT-18 runs, one tool
  // earlier and on scribe-line structures instead of on packages.
  const sigma = clamp(condition.gaugeSigma + 0.02 * Math.abs(condition.chuckOffsetC) / 10, 0.005, 1)
  const parametricYield = clamp(
    1 - 2 * normalCdf(-(1 - recipe.parametricGuardBand) / sigma) - 0.004,
    0,
    1,
  )

  // Contact loss belongs to the probe card, not to the wafer, and telling those
  // apart is most of what a sort engineer does. Worn tips and a card that is not
  // flat both produce failures that look exactly like dead die.
  // The out-of-plane the card SEES: its own error, plus whatever the wafer
  // is doing under it - a wafer the chuck is not holding lifts at the edge.
  const outOfPlaneUm = condition.cardPlanarityUm + waferLiftUm(condition.chuckVacuumKpa)
  const contactYield = clamp(
    1 - 0.06 * clamp(condition.tipWear, 0, 1) - 0.02 * Math.max(outOfPlaneUm - 5, 0),
    0,
    1,
  )

  const sortYield = defectYield * parametricYield * contactYield

  // The map. A worn card lands heaviest at the perimeter; a chuck offset shows
  // as a radial gradient; heavy clustering shows as patches.
  let signature: MapSignature = 'uniform'
  if (outOfPlaneUm > 8 || condition.tipWear > 0.5) signature = 'edge'
  else if (Math.abs(condition.chuckOffsetC) > 4) signature = 'radial'
  else if (recipe.clusterAlpha < 1.2) signature = 'clustered'

  const edgeBias =
    signature === 'edge' ? 0.75 : signature === 'radial' ? 0.88 : signature === 'clustered' ? 0.95 : 1
  const edgeYield = clamp(sortYield * edgeBias, 0, 1)
  const centreYield = clamp(sortYield * (2 - edgeBias), 0, 1)

  return {
    grossDie: gross,
    probedDie: probed,
    edgeLoss: gross - probed,
    defectYield,
    poissonYield: poisson,
    parametricYield,
    effectiveGaugeSigma: sigma,
    contactYield,
    sortYield,
    goodDie: Math.round(probed * sortYield),
    signature,
    centreYield,
    edgeYield,
    waferHours: (probed * recipe.testSeconds) / 3600,
  }
}

export const PROBE_PRESETS: Record<string, { label: string; note: string; recipe: ProbeRecipe }> = {
  baseline: {
    label: 'BASELINE-SORT',
    note: 'Qualified sort: 0.59 cm² die, 3 mm edge exclusion, negative binomial at alpha 3.',
    recipe: { ...DEFAULT_PROBE_RECIPE },
  },
  bigDie: {
    label: 'LARGE DIE',
    note:
      'A 2 cm² die on the same line. Yield falls exponentially in area, which is why die size is ' +
      'the most expensive decision a designer makes.',
    recipe: { ...DEFAULT_PROBE_RECIPE, dieAreaCm2: 2 },
  },
  poissonForecast: {
    label: 'POISSON FORECAST',
    note:
      'The same wafer predicted with Poisson instead. Systematically pessimistic, because real ' +
      'defects cluster and clustered defects waste themselves on die that were already dead.',
    recipe: { ...DEFAULT_PROBE_RECIPE, model: 'poisson' },
  },
  heavyCluster: {
    label: 'HEAVILY CLUSTERED',
    note: 'A scratch or a bad handling event: alpha down to 0.8. Same defect count, better yield.',
    recipe: { ...DEFAULT_PROBE_RECIPE, clusterAlpha: 0.8 },
  },
  wideEdge: {
    label: 'WIDE EDGE EXCLUSION',
    note: '8 mm excluded to avoid edge failures. It also throws away good die on the way.',
    recipe: { ...DEFAULT_PROBE_RECIPE, edgeExclusionMm: 8 },
  },
}
